<template lang="pug">
  .invoice-table-container(:class="{ 'has-data': hasData }")
    //- Error State
    div(v-if="isError")
      yz-empty-state-card(icon="mdi-alert-circle-outline" icon-color="yz-danger-500" :title="$t('error_message.unable_to_load_data')" :description="$t('error_message.please_try_again')" :action-label="$t('retry')" @action-click="fetchInvoices")

    //- Filter Row and Data table
    div(v-else)
      //- Filter Row
      invoice-filter-row.mt-4(ref="invoiceFilterRow" :filters.sync="filterParams" @update:filters="handleFilterChange" @filters-serialized="handleFiltersSerialized")

      //- Data table
      yz-data-table.mt-4(:headers="tableHeaders" :items="invoicesData" item-key="uid" :loading="loading" :server-items-length="totalInvoices" show-select, no-outer-border, radius="0" disable-sort, body-height="52px", header-height="36px", @update:sort-by="sortBy = $event" @update:sort-desc="sortIsDesc = $event" @click:row="onCreateClaim")
        template(v-slot:header.data-table-select)
          yz-checkbox(:indeterminate="someInvoiceSelected" :value="allInvoiceSelected" @change="onToggleSelectAll")

        template(v-slot:item.data-table-select="{ item }")
          yz-checkbox(:value="!!selectedInvoices.find(selected => selected.uid === item.uid)" @change="onToggleSelectInvoice(item)" @click.native.stop)

        template(v-slot:item.amount="{ item }")
          span {{ item.amount | displayPrice($currency) }}

        template(v-slot:item.panel="{ item }")
          yz-badge(v-if="item.panelName" border size="sm") {{ item.panelName }}

        template(v-slot:item.status="{ item }")
          yz-badge(v-if="item.status" size="sm" :color="getInvoiceStatusColor(item.status)" :text-color="getInvoiceStatusTextColor(item.status)")
            yz-dot(:color="getInvoiceStatusDotColor(item.status)" size="6px")
            span.ml-2.capitalize {{ item.status }}

        template(v-slot:item.updatedBy="{ item }")
          yz-badge(v-if="item.updatedBy" border size="sm")
            yz-avatar.mr-1(size="12")
              yz-img(v-if="item.updatedBy.profile_image && item.updatedBy.profile_image.file" :src="`https://img.yezza.io/50x50/${item.updatedBy.profile_image.path}`")
              yz-icon(v-else color="yz-neutral-500") $yzi-user-03
            span {{ item.updatedBy.name }}

        template(v-slot:item.actions="{ item }")
          yz-menu(offset-y close-on-click left attach)
            template(v-slot:activator="{ on, attrs }")
              yz-btn(icon-only, icon-color="yz-neutral-500" v-bind="attrs" v-on="on" icon="$yzi-dots-horizontal")
            yz-list
              yz-list-item(@click.stop="onViewInvoice(item)")
                div {{ $t('panel.view_invoice') }}

              yz-divider

              yz-list-item(@click.stop="onCreateClaim(item)")
                div {{ $t('panel.create_claim') }}

      //- Pagination
      .d-flex.justify-end.pt-3
        yz-pagination(v-model="currentPage" version="yc" :page-size.sync="pageSize" :count="totalInvoices" @input="fetchInvoices")
      // Sortable
      yz-data-table-sorter(v-model="ordering" :headers="tableHeaders" :sort-by="sortBy" :is-desc="sortIsDesc" @input="onUpdateOrdering")

    //- Single Panel Claim Confirmation Dialog
    single-claim-confirmation-dialog(v-if="showConfirmationDialog" v-model="showConfirmationDialog" @confirmed="onConfirmationConfirmed" @canceled="onConfirmationCanceled")

    //- Create Panel Claim Dialog
    create-single-claim-dialog(v-if="showCreateSingleClaimDialog" v-model="showCreateSingleClaimDialog" :panel-name="selectedInvoice.panel.name" :panel-id="selectedInvoice.panel.id" :invoice-amount="selectedInvoice.amount" :invoice-lead-pid="selectedInvoice.lead_pid" :invoice-uid="selectedInvoice.uid" :claim-status="selectedInvoice.claim_status" :visit-date="selectedInvoice.visit_date" @cancel="onClaimDialogCancel" @created="onClaimDialogCreated")

    //- Create Bulk Panel Claim Dialog
    create-bulk-claim-dialog(v-if="showCreateBulkClaimDialog" v-model="showCreateBulkClaimDialog" :parent-invoices="selectedInvoices" @cancel="onBulkClaimDialogCancel" @created="onBulkClaimDialogCreated")

    //- Send Panel Claim Dialog
    send-claim-dialog(v-if="showSendClaimDialog" v-model="showSendClaimDialog" :claim-id="createdClaimId" @close="onSendClaimDialogClosed")

    //- Download Invoice Dialog
    download-invoice-dialog(v-if="showDownloadInvoiceDialog" v-model="showDownloadInvoiceDialog" :invoices="selectedInvoices" @close="onDownloadInvoiceDialogClosed")

    //- Download Receipt Dialog
    download-receipt-dialog(v-if="showDownloadReceiptDialog" v-model="showDownloadReceiptDialog" :invoices="selectedInvoices" @close="onDownloadReceiptDialogClosed")

    //- Invoice Selection Actions (Sticky)
    invoice-selection-actions(:selected-items="selectedInvoices" :show="selectedInvoices.length > 0" @download-invoice="onDownloadInvoice" @download-receipt="onDownloadReceipt" @create-bulk-claim="onCreateBulkClaim")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import CreateSingleClaimDialog from '@/components/clinic/panel/dialogs/single/CreateSingleClaimDialog.vue'
import CreateBulkClaimDialog from '@/components/clinic/panel/dialogs/bulk/CreateBulkClaimDialog.vue'
import SingleClaimConfirmationDialog from '@/components/clinic/panel/dialogs/single/SingleClaimConfirmationDialog.vue'
import SendClaimDialog from '@/components/clinic/panel/dialogs/SendClaimDialog.vue'
import InvoiceSelectionActions from '@/components/clinic/panel/claims/InvoiceSelectionActions.vue'
import InvoiceFilterRow from '@/components/clinic/panel/claims/InvoiceFilterRow.vue'
import DownloadInvoiceDialog from '@/components/clinic/panel/dialogs/DownloadInvoiceDialog.vue'
import DownloadReceiptDialog from '@/components/clinic/panel/dialogs/DownloadReceiptDialog.vue'

export default {
  components: { CreateSingleClaimDialog, CreateBulkClaimDialog, SingleClaimConfirmationDialog, SendClaimDialog, InvoiceSelectionActions, InvoiceFilterRow, DownloadInvoiceDialog, DownloadReceiptDialog },
  props: {
    activeTab: {
      type: Number,
      required: true
    },
    searchText: {
      type: String,
      default: null
    },
    initialPage: {
      type: Number,
      default: 1
    },
    initialFilters: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      // Page state
      loading: false,
      isError: false,
      isInitializing: true,
      // Pagination
      currentPage: this.initialPage,
      pageSize: 10,
      // Sorting
      sortBy: null,
      sortIsDesc: false,
      ordering: null,
      // Data
      totalInvoices: 0,
      invoicesData: [],
      // Dialog states
      showCreateSingleClaimDialog: false,
      showCreateBulkClaimDialog: false,
      showConfirmationDialog: false,
      showSendClaimDialog: false,
      showDownloadInvoiceDialog: false,
      showDownloadReceiptDialog: false,
      // Selection state
      selectedInvoices: [],
      selectedInvoice: null,
      createdClaimData: null,
      // Filters
      filterParams: {}
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      dates: 'clinic/getPanelClaimDates',
      beginDate: 'clinic/getPanelClaimBeginDate',
      untilDate: 'clinic/getPanelClaimUntilDate',
      fields: 'clinic/getPanelClaimInvoiceFields'
    }),
    tableHeaders() {
      const enabledFields = this.fields.filter(field => field.enable)
      const headers = enabledFields.map(field => ({
        text: field.field_name.toUpperCase(),
        value: field.id,
        sort_query_param: field.sort_query_param,
        sortable: field.sortable !== false
      }))
      // Always add actions column at the end
      headers.push({ text: '', value: 'actions', sortable: false, align: 'end' })
      return headers
    },
    allInvoiceSelected() {
      return this.invoicesData.length > 0 && this.invoicesData.every(invoice => this.selectedInvoices.find(selected => selected.uid === invoice.uid))
    },
    someInvoiceSelected() {
      const hasSelected = this.invoicesData.some(invoice => this.selectedInvoices.find(selected => selected.uid === invoice.uid))
      return hasSelected && !this.allInvoiceSelected
    },
    createdClaimId() {
      return this.createdClaimData?.id ?? null
    },
    hasData() {
      return this.invoicesData.length > 0
    }
  },
  async mounted() {
    // Initialize filters from initial prop if provided
    if (this.initialFilters) {
      // Deserialize and set filterParams immediately so they're available for first fetch
      this.filterParams = this.deserializeFilters(this.initialFilters)

      // Then initialize the filter UI components
      if (this.$refs.invoiceFilterRow) {
        await this.$refs.invoiceFilterRow.initializeFiltersFromProps(this.initialFilters)
      }
    }
    await this.fetchInvoices()
    // Mark initialization complete to allow filter changes to trigger refetch
    this.isInitializing = false
  },
  watch: {
    activeTab() {
      this.resetPageAndRefetch()
    },
    dates() {
      this.resetPageAndRefetch()
    },
    currentPage(newPage) {
      this.$emit('page-changed', newPage)
    },
    initialPage(newPage) {
      // Sync with parent when initialPage prop changes (from browser back/forward)
      if (this.currentPage !== newPage) {
        this.currentPage = newPage
      }
    },
    async initialFilters(newFilters) {
      // Handle filter changes from browser back/forward navigation
      if (!newFilters) {
        // Filters cleared - check if we already have empty filters
        const currentFiltersEmpty = !this.filterParams || Object.keys(this.filterParams).length === 0

        if (currentFiltersEmpty) {
          // Already empty, no need to refetch
          return
        }

        // Clear filters and refetch
        this.filterParams = {}
        if (this.$refs.invoiceFilterRow) {
          await this.$refs.invoiceFilterRow.initializeFiltersFromProps(null)
        }
        this.resetPageAndRefetch()
        return
      }

      const deserializedFilters = this.deserializeFilters(newFilters)
      const currentFiltersJson = JSON.stringify(this.filterParams)
      const newFiltersJson = JSON.stringify(deserializedFilters)

      // Only update if filters actually changed
      if (currentFiltersJson !== newFiltersJson) {
        this.filterParams = deserializedFilters
        if (this.$refs.invoiceFilterRow) {
          await this.$refs.invoiceFilterRow.initializeFiltersFromProps(newFilters)
        }
        this.resetPageAndRefetch()
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    deserializeFilters(base64String) {
      // Deserialize base64 encoded string back to filters object
      if (!base64String) {
        return {}
      }
      try {
        const json = decodeURIComponent(atob(base64String))
        return JSON.parse(json)
      } catch (error) {
        console.error('Error deserializing filters:', error)
        this.showMessage({
          message: this.$t('panel.filter_parameters_corrupted'),
          color: 'warning'
        })
        return {}
      }
    },
    resetPageAndRefetch() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
        return
      }

      this.fetchInvoices()
    },
    async fetchInvoices() {
      this.loading = true
      this.isError = false
      const params = {
        page: this.currentPage,
        page_size: this.pageSize,
        ordering: this.ordering || '-created',
        search: this.searchText
      }

      // Add tab-specific filtering
      const tabFilters = this.getTabFilters()
      if (tabFilters) {
        Object.assign(params, tabFilters)
      }

      // Add date range filtering
      if (this.beginDate && this.untilDate) {
        params.begin = this.beginDate
        params.until = this.untilDate
      }

      // Add filter params from FilterRow
      if (this.filterParams && Object.keys(this.filterParams).length > 0) {
        Object.assign(params, this.filterParams)
      }

      try {
        const response = await this.$axios.get(`/panels/${this.company.slug}/invoices`, { params })
        this.invoicesData = response.data.results.map(invoice => {
          return {
            originalInvoice: invoice,
            uid: invoice.uid,
            amount: invoice.amount || 0,
            invoiceNo: invoice.invoice_no,
            panelName: invoice.panel?.name,
            patientName: invoice.name,
            status: this.getStatusByTab(invoice),
            visitDate: this.formatDate(invoice.visit_date),
            dueDate: this.formatDate(invoice.submission_due),
            remarks: invoice.internal_description,
            updatedBy: invoice.updated_by
          }
        })
        this.totalInvoices = response.data.count
        this.$emit('count-updated', { tab: this.activeTab, count: this.totalInvoices, filters: this.filterParams })
      } catch (error) {
        console.error('Error fetching invoices:', error)
        this.isError = true
      } finally {
        this.loading = false
      }
    },
    getTabFilters() {
      // Map tab index to API filter parameters for invoices
      const tabMap = {
        1: { to_submit: 'pending' },
        2: { to_submit: 'overdue' }
      }
      return tabMap[this.activeTab] || null
    },
    getStatusByTab(invoice) {
      // Map status based on active tab
      if (this.activeTab === 1) {
        return 'pending'
      }
      if (this.activeTab === 2) {
        return 'overdue'
      }
      // Return original status for other tabs
      return invoice.claim_status
    },
    formatDate(dateString) {
      if (!dateString) return null
      const date = DateTime.fromISO(dateString)
      return date.toFormat('d MMMM yyyy')
    },
    getInvoiceStatusColor(status) {
      const statusColors = {
        pending: '#FFF7D9',
        overdue: '#FFEBEB'
      }
      return statusColors[status] || 'neutral-200'
    },
    getInvoiceStatusDotColor(status) {
      const statusColors = {
        pending: '#F2BE37',
        overdue: '#F04438'
      }
      return statusColors[status] || 'neutral-200'
    },
    getInvoiceStatusTextColor(status) {
      const textColors = {
        pending: '#CA6C16',
        overdue: '#B52222'
      }
      return textColors[status] || 'neutral-500'
    },
    // Action handlers
    onViewInvoice(item) {
      const route = this.$router.resolve(`/admin/clinic/checkout/${item.originalInvoice.lead_pid}`)
      window.open(route.href, '_blank')
    },
    onCreateClaim(item) {
      // Store the selected item for later use
      this.selectedInvoice = item.originalInvoice

      // Check if panel submission type is bulk - if so, show confirmation dialog
      if (item.originalInvoice.panel?.submission_type === 'bulk') {
        this.showConfirmationDialog = true
      } else {
        // For single submission type, go directly to create claim dialog
        this.showCreateSingleClaimDialog = true
      }
    },
    onUpdateOrdering() {
      this.resetPageAndRefetch()
    },
    onClaimDialogCancel() {
      // Handle dialog cancellation - dialog will close automatically via .sync
    },
    onClaimDialogCreated(claimData) {
      this.createdClaimData = claimData
      this.showSendClaimDialog = true
      this.fetchInvoices()
      this.$emit('updated')
    },
    onConfirmationConfirmed() {
      // User confirmed, now open the create claim dialog
      this.showCreateSingleClaimDialog = true
    },
    onConfirmationCanceled() {
      // User canceled - dialog will close automatically
    },
    onSendClaimDialogClosed() {
      this.createdClaimData = null
    },
    // Data table select methods
    onToggleSelectAll() {
      if (this.allInvoiceSelected) {
        // Remove current page items from selection
        this.invoicesData.forEach(invoice => {
          const existingIndex = this.selectedInvoices.findIndex(selected => selected.uid === invoice.uid)
          if (existingIndex !== -1) {
            this.selectedInvoices.splice(existingIndex, 1)
          }
        })
      } else {
        // Add current page items to selection (avoid duplicates)
        this.invoicesData.forEach(invoice => {
          const alreadySelected = this.selectedInvoices.find(selected => selected.uid === invoice.uid)
          if (!alreadySelected) {
            this.selectedInvoices.push(invoice.originalInvoice)
          }
        })
      }
    },
    onToggleSelectInvoice(item) {
      const existingIndex = this.selectedInvoices.findIndex(selected => selected.uid === item.uid)
      if (existingIndex !== -1) {
        this.selectedInvoices.splice(existingIndex, 1)
      } else {
        this.selectedInvoices.push(item.originalInvoice)
      }
    },
    // Selection actions handlers
    onDownloadInvoice() {
      if (!this.selectedInvoices || this.selectedInvoices.length === 0) {
        this.showMessage(['error', 'No invoices selected'])
        return
      }
      this.showDownloadInvoiceDialog = true
    },
    onDownloadReceipt() {
      if (!this.selectedInvoices || this.selectedInvoices.length === 0) {
        this.showMessage(['error', 'No invoices selected'])
        return
      }
      this.showDownloadReceiptDialog = true
    },
    onDownloadInvoiceDialogClosed() {
      this.showDownloadInvoiceDialog = false
    },
    onDownloadReceiptDialogClosed() {
      this.showDownloadReceiptDialog = false
    },
    onCreateBulkClaim() {
      this.showCreateBulkClaimDialog = true
    },
    onBulkClaimDialogCancel() {
      // Handle bulk claim dialog cancellation - dialog will close automatically via .sync
    },
    onBulkClaimDialogCreated(claimData) {
      this.createdClaimData = claimData
      this.showSendClaimDialog = true
      this.fetchInvoices()
      this.$emit('updated')
      this.selectedInvoices = []
    },
    // Filter handlers
    handleFilterChange(filters) {
      this.filterParams = filters
      // Don't refetch during initial mount to avoid double API call
      if (!this.isInitializing) {
        this.resetPageAndRefetch()
      }
    },
    handleFiltersSerialized(serialized) {
      // Emit to parent to update URL
      this.$emit('filters-serialized', serialized)
    }
  }
}
</script>

<style scoped>
.invoice-table-container {
  position: relative;
}
.invoice-table-container.has-data ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
</style>
